import { useState, useEffect } from 'react'
import s from './DealAnalysisContainer.scss'
import { NumericFormat } from 'react-number-format'
import { getCurrency } from 'utils/authService'
import { Container, Row, Col, Button } from 'reactstrap'
import { KeyValuePairs, InputContainer } from 'components/ideal-core-ui'
import { useUnsavedChanges, useLoading } from 'hooks'
import { useParams } from 'react-router-dom'
import { makePutRequest } from 'utils/api'
import { useImmer } from 'use-immer'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import * as ACT from '../actions'

const DealAnalysis = () => {
  const { id } = useParams()
  const [, withLoadingLocks] = useLoading()
  const [isPristine, setIsPristine] = useState(true)
  const [data, setData] = useImmer({})
  const dispatch = useDispatch()
  const { analysis } = useSelector(state => state.deal)
  useUnsavedChanges(!isPristine, saveChanges)

  useEffect(() => {
    dispatch({
      type: ACT.FETCH_DEAL_ANALYSIS_REQUESTED,
      payload: {
        dealId: id
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!analysis) return
    setData(analysis)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis])

  const changeProperty = (value, property) => {
    setData({
      ...data,
      [property]: value
    })
    setIsPristine(false)
  }

  async function saveChanges(cb) {
    try {
      const response = await withLoadingLocks(
        makePutRequest(`/v1/deals/margin-analysis/${id}`, data)
      )

      setIsPristine(true)

      setData(response)

      dispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Saved successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      dispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem saving',
          level: 'error'
        }
      })
    }

    cb && cb()
  }

  return (
    <Container>
      <h5 className={s.marginBottom30}>Opportunity Margin Analysis</h5>
      <div className={s.scrollable}>
        <Col lg="8" sm="8">
          <Row>
            <Col lg="8" sm="8">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Opportunity Value',
                    keyClass: [s.bold],
                    valueClass: [s.bold],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={data.dealValue || 0}
                        prefix={getCurrency().symbol}
                        displayType="text"
                      />
                    )
                  }
                ]}
              />
            </Col>
            <Col lg="4" sm="4">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Rev/Hr',
                    keyClass: [s.customLabel],
                    valueClass: [s.customLabel],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={data.revPerHour || 0}
                        prefix={getCurrency().symbol}
                        displayType="text"
                      />
                    )
                  }
                ]}
              />
            </Col>
          </Row>
          <Row>
            <p className={s.subtitle}>Direct (Prime) Job Costs</p>
          </Row>
          <Row className={s.section}>
            <Col lg="8" sm="8">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Engineering',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.engineering || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'engineering')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.engineeringPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Labor',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.labor || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'labor')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.engineeringPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Materials',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.materials || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'materials')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.materialsPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Equipment',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.equipment || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'equipment')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.equipmentPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Subcontractors',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.subcontractors || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'subcontractors')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.subcontractorsPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Contingency',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.contingency || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'contingency')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.contingencyPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Other Direct Job Costs',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.otherDirectJobCosts || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'otherDirectJobCosts')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>
                          {data.otherDirectJobCostsPercent || 0}%
                        </span>
                      </>
                    )
                  }
                ]}
              />
            </Col>
            <Col lg="4" sm="4">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Hours',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <InputContainer
                        type="number"
                        placeholder="#"
                        cb={value => changeProperty(value, 'engineeringHours')}
                        text={data.engineeringHours || ''}
                      />
                    )
                  },
                  {
                    key: 'Hours',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <InputContainer
                        type="number"
                        placeholder="#"
                        cb={value => changeProperty(value, 'laborHours')}
                        text={data.laborHours || ''}
                      />
                    )
                  },
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: <InputContainer type="text" />
                  },
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: <InputContainer type="text" />
                  },
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: <InputContainer type="text" />
                  },
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: <InputContainer type="text" />
                  },
                  {
                    key: 'Description',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <InputContainer
                        type="text"
                        placeholder="Description"
                        cb={value => changeProperty(value, 'description')}
                        text={data.description || ''}
                      />
                    )
                  }
                ]}
              />
            </Col>
          </Row>
          <Row className={s.section}>
            <Col lg="8" sm="8">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Total Direct Job Costs',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.totalDirectJobCosts || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => <span className={s.minWidth}>{value}</span>}
                        />
                        <span className={s.percentage}>
                          {data.totalDirectJobCostsPercent || 0}%
                        </span>
                      </>
                    )
                  },
                  {
                    key: 'Gross Profit',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.grossProfit || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => <span className={s.minWidth}>{value}</span>}
                        />
                        <span className={s.percentage}>{data.grossProfitPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Indirect Job Costs (General Conditions)',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          placeholder="Enter Value"
                          decimalScale={2}
                          allowNegative={false}
                          value={data.indirectJobCosts || ''}
                          prefix={getCurrency().symbol}
                          onChange={formattedValue => {
                            const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                            changeProperty(+stripValue, 'indirectJobCosts')
                          }}
                          style={{
                            width: 140,
                            padding: 2
                          }}
                        />
                        <span className={s.percentage}>{data.indirectJobCostsPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Construction Profit',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.constructionProfit || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => <span className={s.minWidth}>{value}</span>}
                        />
                        <span className={s.percentage}>{data.constructionProfitPercent || 0}%</span>
                      </>
                    )
                  },
                  {
                    key: 'Division Overhead',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.divisionOverhead || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => <span className={s.minWidth}>{value}</span>}
                        />
                        <span className={s.percentage}>
                          <NumericFormat
                            decimalScale={2}
                            value={data.divisionOverheadPercent || ''}
                            suffix="%"
                            className={s.smallInput}
                            onChange={formattedValue => {
                              const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                              changeProperty(+stripValue, 'divisionOverheadPercent')
                            }}
                          />
                        </span>
                      </>
                    )
                  },
                  {
                    key: 'Corporate Overhead',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.corporateOverhead || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => <span className={s.minWidth}>{value}</span>}
                        />
                        <span className={s.percentage}>
                          <NumericFormat
                            decimalScale={2}
                            value={data.corporateOverheadPercent || ''}
                            suffix="%"
                            className={s.smallInput}
                            onChange={formattedValue => {
                              const stripValue = formattedValue.target.value.replace(/[^\d.-]/g, '')
                              changeProperty(+stripValue, 'corporateOverheadPercent')
                            }}
                          />
                        </span>
                      </>
                    )
                  }
                ]}
              />
            </Col>
            <Col lg="4" sm="4">
              <KeyValuePairs
                pairs={[
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: 'empty'
                  },
                  {
                    key: '',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    sectionClass: [s.hidden],
                    value: 'empty'
                  },
                  {
                    key: 'Hours',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <InputContainer
                        type="number"
                        placeholder="#"
                        cb={value => changeProperty(value, 'indirectJobCostsHours')}
                        text={data.indirectJobCostsHours || ''}
                      />
                    )
                  }
                ]}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="8" sm="8">
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Net Operating Income',
                    keyClass: [s.bold],
                    valueClass: [s.bold],
                    value: (
                      <>
                        <NumericFormat
                          thousandSeparator
                          decimalScale={2}
                          value={data.netOperatingIncome || 0}
                          prefix={getCurrency().symbol}
                          displayType="text"
                          renderText={value => (
                            <span
                              className={cn(s.minWidth, s.green, {
                                [s.red]: +(data.netOperatingIncome || 0) < 0
                              })}
                            >
                              {value}
                            </span>
                          )}
                        />
                        <span
                          className={cn(s.percentage, s.green, {
                            [s.red]: +(data.netOperatingIncome || 0) < 0
                          })}
                        >
                          {data.netOperatingIncomePercent || 0}%
                        </span>
                      </>
                    )
                  }
                ]}
              />
            </Col>
          </Row>
          <Button onClick={() => saveChanges()} className={s.saveBtn}>
            Save
          </Button>
        </Col>
      </div>
    </Container>
  )
}

export default DealAnalysis
